import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, ViewEncapsulation, computed, inject, signal } from '@angular/core';

type Op = '+' | '−' | '×' | '÷';

/** Persian (۰-۹) and Arabic-Indic (٠-٩) digits → ASCII 0-9. */
function toEnDigits(s: string): string {
  return s
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}

/**
 * Toolbox Calculator. Faithful to the OTA-B2B-5 design, made fully functional
 * with real-tool keyboard support: digits + `.`, operators `+ - * /`,
 * Enter/`=` equals, Backspace deletes a digit, Escape/`c` clears, `%` percent,
 * `n` negate. Runs inside a focusable window; key events are captured on the
 * host so typing works whenever the calculator has focus.
 */
@Component({
  selector: 'app-calculator',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './calculator.component.html',
  host: { tabindex: '0', class: 'calc-host' },
})
export class CalculatorComponent implements AfterViewInit {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly display = signal('0');
  readonly prev = signal<number | null>(null);
  readonly op = signal<Op | null>(null);
  readonly fresh = signal(true);

  /** An operator button is lit while it's the pending op and no new digit yet. */
  readonly activeOp = computed(() => (this.fresh() ? this.op() : null));

  /** Shrink the display font as the number grows so long results fit without
   *  truncation. Calibrated so ~8 grouped chars sit at the full 32px; longer
   *  values scale down (floored at 13px). */
  readonly displayFontPx = computed(() =>
    Math.min(32, Math.max(13, Math.floor(256 / Math.max(1, this.displayText().length)))));

  /** Screen text with thousands separators — grouping the integer part while
   *  preserving the decimal part (and a trailing '.' mid-entry). Raw exponential
   *  results and 'Error' are shown as-is. `display` stays the raw math string. */
  readonly displayText = computed(() => {
    const s = this.display();
    if (s === 'Error' || s.includes('e') || s.includes('E')) return s;
    const neg = s.startsWith('-');
    const body = neg ? s.slice(1) : s;
    const [intPart, decPart] = body.split('.');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const out = s.includes('.') ? grouped + '.' + (decPart ?? '') : grouped;
    return (neg ? '-' : '') + out;
  });

  private fmt(n: number): string {
    if (!isFinite(n)) return 'Error';
    return String(+parseFloat(n.toPrecision(12)));
  }
  private calc(a: number, b: number, o: Op): number {
    return o === '+' ? a + b : o === '−' ? a - b : o === '×' ? a * b : o === '÷' ? a / b : b;
  }

  inputDigit(d: string): void {
    if (this.display() === 'Error') this.clearAll();
    const cur = this.display();
    const fresh = this.fresh();
    this.display.set(
      fresh || cur === '0'
        ? (d === '.' ? '0.' : d)
        : (d === '.' && cur.includes('.') ? cur : cur + d),
    );
    this.fresh.set(false);
  }

  chooseOp(o: Op): void {
    const cur = parseFloat(this.display());
    if (this.op() != null && !this.fresh()) {
      const r = this.calc(this.prev() as number, cur, this.op() as Op);
      this.prev.set(r);
      this.display.set(this.fmt(r));
    } else {
      this.prev.set(cur);
    }
    this.op.set(o);
    this.fresh.set(true);
  }

  equals(): void {
    if (this.op() == null || this.prev() == null) return;
    const cur = parseFloat(this.display());
    this.display.set(this.fmt(this.calc(this.prev() as number, cur, this.op() as Op)));
    this.prev.set(null);
    this.op.set(null);
    this.fresh.set(true);
  }

  clearAll(): void {
    this.display.set('0');
    this.prev.set(null);
    this.op.set(null);
    this.fresh.set(true);
  }
  negate(): void { this.display.update((c) => this.fmt(parseFloat(c) * -1)); }
  percent(): void { this.display.update((c) => this.fmt(parseFloat(c) / 100)); this.fresh.set(true); }

  /** Backspace — drop the last entered character (real-calculator nicety). */
  backspace(): void {
    if (this.fresh() || this.display() === 'Error') return;
    const c = this.display();
    const next = c.length <= 1 || (c.length === 2 && c.startsWith('-')) ? '0' : c.slice(0, -1);
    this.display.set(next);
    if (next === '0') this.fresh.set(true);
  }

  /** Grab focus when the window opens so the physical keyboard works right away
   *  (the host must be focused for its keydown to fire). */
  ngAfterViewInit(): void {
    queueMicrotask(() => (this.host.nativeElement as HTMLElement).focus());
  }

  /** Any click inside the calculator returns keyboard focus to the host. */
  @HostListener('pointerdown')
  onPointerDown(): void { (this.host.nativeElement as HTMLElement).focus(); }

  @HostListener('keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    // Normalise Persian/Arabic-Indic digit keys to ASCII first.
    const k = toEnDigits(e.key);
    if (k >= '0' && k <= '9') { this.inputDigit(k); }
    else if (k === '.' || k === ',') { this.inputDigit('.'); }
    else if (k === '+') { this.chooseOp('+'); }
    else if (k === '-') { this.chooseOp('−'); }
    else if (k === '*' || k === 'x' || k === 'X') { this.chooseOp('×'); }
    else if (k === '/') { this.chooseOp('÷'); }
    else if (k === 'Enter' || k === '=') { this.equals(); }
    else if (k === 'Backspace') { this.backspace(); }
    else if (k === 'Escape' || k === 'c' || k === 'C') { this.clearAll(); }
    else if (k === '%') { this.percent(); }
    else if (k === 'n' || k === 'N') { this.negate(); }
    else { return; }
    e.preventDefault();
  }
}
